import { forwardRef, type ForwardedRef, type MouseEvent } from 'react';
import { MenuList, MenuItem, styled } from '@mui/material';
import Checkbox from '../Checkbox';

type StringOrNumber = string | number;
export type Option<T extends StringOrNumber> = { label: string; value: T };

const Label = styled('span')(() => ({
  marginLeft: 8,
}));

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  padding: 8,
  font: '400 0.9375rem NeoSansStd',
  color: 'rgb(34, 64, 96)',
  [theme.breakpoints.down('sm')]: {
    minHeight: 32,
  },
}));

export type TableFilterSelectProps<T extends StringOrNumber> = {
  value: T[];
  options: Option<T>[];
  showSelectAllOption?: boolean;
  selectAllLabel?: string;
  onChange: (newValue: T[]) => void;
};

const TableFilterSelectView = <T extends StringOrNumber>(
  {
    options,
    showSelectAllOption,
    selectAllLabel = 'Select all',
    value,
    onChange,
  }: TableFilterSelectProps<T>,
  ref: ForwardedRef<HTMLUListElement>
) => {
  const selectedAll = options.every((option) => value.includes(option.value));
  const indeterminated = !selectedAll && value.length > 0;

  const handleClickMenuItem = (optionValue: T) => {
    let newValue: T[] = [];
    if (value.includes(optionValue)) {
      newValue = value.filter((el) => el !== optionValue);
    } else {
      newValue = value.concat(optionValue);
    }

    onChange(newValue);
  };

  const handleSelectAll = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    event.preventDefault();
    console.log('handleSelectAll');
    let newValue: T[] = [];

    if (selectedAll) {
      newValue = [];
    } else {
      newValue = options.map((option) => option.value);
    }

    onChange(newValue);
  };

  return (
    <MenuList ref={ref}>
      {showSelectAllOption && (
        <StyledMenuItem onClick={handleSelectAll}>
          <Checkbox checked={selectedAll} indeterminate={indeterminated} />
          <Label>{selectAllLabel}</Label>
        </StyledMenuItem>
      )}
      {options.map((option) => {
        const selected = value.includes(option.value);

        return (
          <StyledMenuItem
            key={option.value}
            selected={selected}
            onClick={() => handleClickMenuItem(option.value)}
          >
            <Checkbox checked={selected} />
            <Label>{option.label}</Label>
          </StyledMenuItem>
        );
      })}
    </MenuList>
  );
};

const TableFilterSelect = forwardRef(TableFilterSelectView);

export default TableFilterSelect;
